import { useLayoutEffect, useRef } from "react";
import { Box, Link, Typography } from "@mui/material";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import dayjs from "dayjs";
import { Link as RouterLink } from "react-router";
import { InquiryAuthor, InquiryDetail, InquiryMessage } from "./types";
import { INK, PROOF_COLORS } from "./styles";
import InquiryAttachment from "./InquiryAttachment";
import { formatCurrency } from "../../utils/formatCurrency";

const STYLES: Record<InquiryAuthor, { label: string; align: string; bubble: object; floatingMeta: object }> = {
  BUYER: { label: "Buyer", align: 'flex-start', bubble: { bgcolor: '#fff', border: `1px solid ${INK.border}` }, floatingMeta: { left: '100%', ml: 1 } },
  SELLER: { label: "Seller", align: 'flex-end', bubble: { bgcolor: INK.tint }, floatingMeta: { right: '100%', mr: 1 } },
  ARBITER: { label: "Arbiter", align: 'center', bubble: { bgcolor: INK.divider }, floatingMeta: { left: '100%', ml: 1 } },
};

function getAttachments(message: InquiryMessage): { name: string; url: string }[] {
  if (message.documents.length > 0) return message.documents;
  return message.sentAttachment ? [message.sentAttachment] : [];
}

function getAuthor(message: InquiryMessage): string {
  const label = STYLES[message.postedBy].label;
  return message.postedBy === 'SELLER' ? message.sentBy ?? label : label;
}

type ProofLine = { sent: boolean; at: string | null; text: string; link: string | null };

type ThreadEntry = InquiryMessage | ProofLine;

function withProofLines(messages: InquiryMessage[], proof: InquiryDetail['proof'], proofOwed: boolean): ThreadEntry[] {
  const entries: ThreadEntry[] = [...messages];
  if (!proof) return entries;
  const asks = proof.askedAt.length ? proof.askedAt : [null];
  const lines: ProofLine[] = [
    ...asks.map((at, i) => ({ sent: false, at, text: 'PayPal asked for proof', link: proofOwed && i === asks.length - 1 ? 'Submit evidence' : null })),
    ...(proof.evidenceSentAt ? [{
      sent: true,
      at: proof.evidenceSentAt,
      text: proof.evidenceSentBy ? `Evidence sent by ${proof.evidenceSentBy}` : 'Evidence sent in PayPal',
      link: proof.evidenceSentBy ? 'View' : null,
    }] : []),
  ];
  for (const line of lines) {
    const at = line.at ?? proof.evidenceSentAt;
    const next = at ? entries.findIndex((entry) => entry.at && dayjs(entry.at).isAfter(at)) : -1;
    entries.splice(next < 0 ? entries.length : next, 0, line);
  }
  return entries;
}

function authorOf(entry: ThreadEntry | undefined): InquiryAuthor | null {
  return entry && 'postedBy' in entry ? entry.postedBy : null;
}

export default function InquiryThread({ inquiryId, evidenceHref, messages, proof, proofOwed, onRetry, onUndo }: { inquiryId: number; evidenceHref: string | null; messages: InquiryMessage[]; proof: InquiryDetail['proof']; proofOwed: boolean; onRetry: () => void; onUndo: () => void }) {
  const listRef = useRef<HTMLDivElement>(null);
  const sending = messages[messages.length - 1]?.pending === true;
  const entries = withProofLines(messages, proof, proofOwed);
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = 0;
  }, [inquiryId, sending]);
  return (
    <Box ref={listRef} sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column-reverse', p: '18px 22px', pb: 1 }}>
      <Box sx={entries.length === 0 ? { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' } : { display: 'flex', flexDirection: 'column', flexShrink: 0, mb: 'auto' }}>
      {entries.length === 0 && <Typography sx={{ fontSize: 12, color: INK.muted }}>No messages yet</Typography>}
      {entries.map((entry, i) => {
        if (!('postedBy' in entry)) {
          return (
            <Box key={i} sx={{ alignSelf: 'center', mt: i === 0 ? 0 : '12px', p: '4px 12px', border: '1px solid', borderRadius: '999px', fontSize: 12, textAlign: 'center', ...PROOF_COLORS[entry.sent ? 'sent' : 'requested'] }}>
              {entry.text}
              {entry.at && ` · ${dayjs(entry.at).format('MMM D, HH:mm')}`}
              {entry.link && evidenceHref && <> · <Link component={RouterLink} to={evidenceHref} underline="none" sx={{ fontSize: 12, fontWeight: 600 }}>{entry.link}</Link></>}
            </Box>
          );
        }
        const message = entry;
        const style = STYLES[message.postedBy];
        const author = getAuthor(message);
        const sameAsPrevious = authorOf(entries[i - 1]) === message.postedBy;
        const lastOfRun = authorOf(entries[i + 1]) !== message.postedBy;
        const metaShown = lastOfRun || message.pending || !!message.failed;
        return (
          <Box
            key={i}
            aria-label={`${style.label} message`}
            sx={{
              alignSelf: style.align,
              alignItems: style.align,
              maxWidth: '78%',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              mt: i === 0 ? 0 : sameAsPrevious ? '4px' : '12px',
              opacity: message.pending ? 0.6 : 1,
              '&:hover > :last-child': { visibility: 'visible' },
            }}
          >
            <Box
              sx={{ ...style.bubble, ...(message.failed && { border: `1px solid ${INK.danger}` }), p: '10px 14px', borderRadius: '12px', fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-line', overflowWrap: 'anywhere', wordBreak: 'break-word', minWidth: 0, color: INK.text }}
            >
              {message.offer && <Box sx={{ fontSize: 11, fontWeight: 600, color: '#b54708', mb: '4px' }}>Offer · {formatCurrency(message.offer.amount, message.offer.currency)}</Box>}
              {message.text}
              {getAttachments(message).map((attachment) => <InquiryAttachment key={attachment.url} attachment={attachment} />)}
              {message.attachmentName && <Box sx={{ fontSize: 12, color: INK.muted, mt: '4px' }}>{message.attachmentName}</Box>}
            </Box>
            <Box sx={{ fontSize: 11, color: message.failed ? INK.danger : INK.muted, display: 'flex', alignItems: 'center', gap: '4px', ...(metaShown ? { mt: '2px', px: '14px' } : { position: 'absolute', top: '50%', transform: 'translateY(-50%)', whiteSpace: 'nowrap', visibility: 'hidden', ...style.floatingMeta }) }}>
              {message.held ? (
                <><AccessTimeOutlined sx={{ fontSize: 11 }} />Sending in 5 s · <Link component="button" onClick={onUndo} sx={{ fontSize: 11, verticalAlign: 'baseline' }}>Undo</Link></>
              ) : message.pending ? (
                <><AccessTimeOutlined sx={{ fontSize: 11 }} />Sending</>
              ) : message.failed ? (
                <span>Failed: {message.failed} · <Link component="button" onClick={onRetry} sx={{ fontSize: 11, verticalAlign: 'baseline' }}>Retry</Link></span>
              ) : (
                <>{author} · {dayjs(message.at).format('MMM D, HH:mm')}</>
              )}
            </Box>
          </Box>
        );
      })}
      </Box>
    </Box>
  );
}
